import { type Location } from "@/gql/localities";
import { logger } from "@/utils/logger";

// Server-side in-memory cache for locations data by city
const locationCache = new Map<string, Location[]>();

// Store locations for a specific city
export function setLocationsForCity(city: string, locations: Location[]): void {
	locationCache.set(city.toLowerCase(), locations);
}

// Get locations for a specific city
export function getLocationsForCity(city: string): Location[] | undefined {
	return locationCache.get(city.toLowerCase());
}

// Normalize text for matching by removing spaces, dashes, and converting to lowercase
function normalizeForMatching(text: string): string {
	return text
		.toLowerCase()
		.trim()
		.replace(/[\s\-_]/g, "");
}

// Remove city prefix from location name if present
function removeCityPrefix(locationName: string, city: string): string {
	const normalizedCity = normalizeForMatching(city);
	const normalizedLocation = normalizeForMatching(locationName);

	// Check if location starts with city name followed by dash or other separators
	if (normalizedLocation.startsWith(normalizedCity)) {
		const withoutPrefix = normalizedLocation.slice(normalizedCity.length);
		// Remove any leading separators after city name
		return withoutPrefix.replace(/^[\-_]+/, "");
	}

	return normalizedLocation;
}

// Find a location by name within a city (fuzzy matching with space/dash normalization)
export function findLocationByName(city: string, locationName: string): Location | undefined {
	const cityLocations = getLocationsForCity(city);
	if (!cityLocations) {
		logger.debug(`⚠️ No locations cached for city: ${city}`);
		return undefined;
	}

	const searchName = locationName.toLowerCase().trim();
	const normalizedSearch = normalizeForMatching(locationName);
	const searchWithoutCityPrefix = removeCityPrefix(locationName, city);

	// First try exact match (original)
	let match = cityLocations.find(loc => loc.name.toLowerCase() === searchName);

	if (match) {
		return match;
	}

	// Try normalized exact match (spaces/dashes removed)
	match = cityLocations.find(loc => normalizeForMatching(loc.name) === normalizedSearch);

	if (match) {
		return match;
	}

	// Try matching without city prefix
	if (searchWithoutCityPrefix !== normalizedSearch) {
		match = cityLocations.find(loc => {
			const normalizedLocName = normalizeForMatching(loc.name);
			const locWithoutCityPrefix = removeCityPrefix(loc.name, city);
			return locWithoutCityPrefix === searchWithoutCityPrefix || normalizedLocName === searchWithoutCityPrefix;
		});

		if (match) {
			logger.debug(`✅ Match found without city prefix for "${locationName}": ${match.name}`);
			return match;
		}
	}

	// Try partial match (location name contains search term) - original
	match = cityLocations.find(loc => loc.name.toLowerCase().includes(searchName));

	if (match) {
		logger.debug(`✅ Partial match found for "${locationName}": ${match.name}`);
		return match;
	}

	// Try normalized partial match
	match = cityLocations.find(loc => normalizeForMatching(loc.name).includes(normalizedSearch));

	if (match) {
		logger.debug(`✅ Normalized partial match found for "${locationName}": ${match.name}`);
		return match;
	}

	// Try reverse partial match (search term contains location name) - original
	match = cityLocations.find(loc => searchName.includes(loc.name.toLowerCase()));

	if (match) {
		logger.debug(`✅ Reverse partial match found for "${locationName}": ${match.name}`);
		return match;
	}

	// Try normalized reverse partial match
	match = cityLocations.find(loc => normalizedSearch.includes(normalizeForMatching(loc.name)));

	if (match) {
		logger.debug(`✅ Normalized reverse partial match found for "${locationName}": ${match.name}`);
		return match;
	}

	logger.debug(`❌ No match found for location "${locationName}" in city "${city}"`);
	return undefined;
}

// Find multiple locations by names
export function findLocationsByNames(city: string, locationNames: string[]): Location[] {
	const matches: Location[] = [];

	for (const name of locationNames) {
		const match = findLocationByName(city, name);
		if (match) {
			matches.push(match);
		}
	}

	logger.debug(`🔍 Found ${matches.length}/${locationNames.length} location matches - ${JSON.stringify(matches)}`);
	return matches;
}

// Extract GPS coordinates from a location
export function getGpsFromLocation(location: Location): { lat: number; lng: number } | null {
	if (!location.gps?.coordinates || location.gps.coordinates.length < 2) {
		logger.debug(`⚠️ No valid GPS coordinates for location: ${location.name}`);
		return null;
	}

	const [lng, lat] = location.gps.coordinates; // GeoJSON format: [longitude, latitude]
	return { lat, lng };
}

// Get GPS coordinates for multiple locations and return centroid
export function getCentroidFromLocations(locations: Location[]): { lat: number; lng: number } | null {
	const validCoords = locations
		.map(getGpsFromLocation)
		.filter((coord): coord is { lat: number; lng: number } => coord !== null);

	if (validCoords.length === 0) {
		logger.debug(`⚠️ No valid GPS coordinates found in ${locations.length} locations`);
		return null;
	}

	// Calculate centroid
	const totalLat = validCoords.reduce((sum, coord) => sum + coord.lat, 0);
	const totalLng = validCoords.reduce((sum, coord) => sum + coord.lng, 0);

	const centroid = {
		lat: totalLat / validCoords.length,
		lng: totalLng / validCoords.length,
	};

	return centroid;
}

// Clear cache for a specific city
export function clearCacheForCity(city: string): void {
	locationCache.delete(city.toLowerCase());
}

// Clear all cache
export function clearAllCache(): void {
	locationCache.clear();
}

// Get cache statistics
export function getCacheStats(): { cities: string[]; totalLocations: number } {
	const cities = Array.from(locationCache.keys());
	const totalLocations = Array.from(locationCache.values()).reduce((sum, locations) => sum + locations.length, 0);

	return { cities, totalLocations };
}
